<template>
  <div v-if="loading" class="margin-top-xs">
    <div v-for="index in 4" :key="index" class="margin-top-xxs margin-right margin-bottom-xxs margin-left">
      <Skeleton height="172px" width="100%" />
    </div>
  </div>
  <template v-else-if="!error">
    <div v-if="orders.length" id="orders" class="medium-margin-top-m">
      <OrderCard v-for="(order, index) in displayedOrders" :key="index" class="margin-bottom-xl" :order="order" />
    </div>
    <template v-else>
      <div
        class="grid-x no-orders align-center"
        :class="[
          'small-margin-top-xs medium-margin-top-s small-margin-bottom-l medium-margin-bottom-m',
          'small-padding-top-m small-padding-bottom-m medium-padding-top-l medium-padding-bottom-l',
        ]"
      >
        <div class="cell medium-7 text-center" role="alert" aria-live="polite">
          {{ onlineHistory.noOrdersMessage }}
        </div>
        <NuxtLink id="order-lookup-link" :to="{ name: 'Order Status' }" class="small-11 medium-6 button button-small primary margin-top-m">
          {{ orderLookupLabel }}
        </NuxtLink>
      </div>
    </template>
  </template>
</template>

<script setup>
import { ref, computed, onUnmounted, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { fireTag } from '@vue-util/tagManager';
import Skeleton from '@atomic-ui/skeleton/Skeleton';
import { ORDER_HISTORY, PPE_ERROR_TAG } from 'config';
import OrderCard from '@/components/common/OrderCard';
import { useOnlineOrderHistoryStore } from '@/stores/ppe/useOnlineOrderHistoryStore';
import { useOrderHistoryStore } from '@/stores/ppe/useOrderHistoryStore';

const route = useRoute();
const orderHistoryStore = useOrderHistoryStore();
const onlineOrderHistoryStore = useOnlineOrderHistoryStore();

const currentBatchOfOrders = ref(1);
const batchSize = 40;
const { onlineHistory, orderLookupLabel } = ORDER_HISTORY;

const orders = computed(() => onlineOrderHistoryStore.orders);
const error = computed(() => onlineOrderHistoryStore.error);
const loading = computed(() => onlineOrderHistoryStore.isLoading);
const selectedDate = computed(() => orderHistoryStore.selectedDate);
const displayedOrders = computed(() => orders.value.slice(0, currentBatchOfOrders.value * batchSize));
const updateOnlineOrderHistoryApiError = computed(() => onlineOrderHistoryStore.updateOnlineOrderHistoryApiError);

const getPurchases = (payload) => onlineOrderHistoryStore.GET_ONLINE_ORDER_HISTORY(payload);
const clearStore = () => onlineOrderHistoryStore.CLEAR_ONLINE_HISTORY();

const loadPurchases = async (dateFilterUpdated = false) => {
  const selectedDateRange = dateFilterUpdated ? route.query.daterange : selectedDate.value['online-history']?.dateRange;
  await getPurchases({
    dateRange: selectedDateRange || 6,
  });

  if (updateOnlineOrderHistoryApiError.value) {
    fireTag('link', {
      ...PPE_ERROR_TAG.error,
      error_code: [updateOnlineOrderHistoryApiError.value.errorCode || PPE_ERROR_TAG.defaultErrorCode],
      error_message: [updateOnlineOrderHistoryApiError.value.message],
    });
  }
};

const windowScroll = () => {
  const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
  const windowHeight = document.documentElement.clientHeight || document.body.clientHeight;
  const allOrders = document.getElementById('orders');
  const lastOrder = allOrders?.lastElementChild;
  const endOfList = allOrders?.offsetTop + (allOrders?.offsetHeight - lastOrder?.offsetHeight);
  const reachEndOfList = windowHeight + scrollTop >= endOfList;
  const moreOrdersAvailable = currentBatchOfOrders.value * batchSize < orders.value.length;
  if (reachEndOfList && moreOrdersAvailable) {
    currentBatchOfOrders.value += 1;
  }
  onlineOrderHistoryStore.orderHistoryScrollTop = scrollTop;
};

onMounted(() => {
  window.addEventListener('scroll', windowScroll);
  loadPurchases();
  fireTag('view', {
    ...ORDER_HISTORY.onlineHistoryTag,
    active_order: false,
  });
});
onUnmounted(() => {
  window.removeEventListener('scroll', windowScroll);
  clearStore();
});
watch(
  orders,
  () => {
    setTimeout(() => {
      window.scrollTo(0, onlineOrderHistoryStore.orderHistoryScrollTop);
    }, 0);
  },
  { deep: true },
);

watch(
  () => route.query.daterange,
  () => {
    if (route.name !== 'online-history') return;
    loadPurchases(true);
  },
);
</script>

<style lang="scss" scoped>
.no-orders {
  background-color: $background-color;
}
</style>
